'use client';

import * as Accordion from '@radix-ui/react-accordion';
import classNames from 'classnames';
import { LucideChevronRight } from 'lucide-react';
import { useEffect, useLayoutEffect, useState } from 'react';

import { Link } from '@/components/Link/Link';
import { useScrollSpy } from '@/src/lib/useScrollSpy';
import { slugify } from '@/util/strings/slugify';

import type { Heading } from '../types';

import styles from './AccordionTableOfContents.module.scss';

interface Props {
    expand?: boolean;
    headings: Heading[];
    className?: string;
    onItemSelected?: (id: string) => void;
}

export function AccordionTableOfContents({ expand, headings, className, onItemSelected }: Props) {
    const [activeElement, setActiveElement] = useState<HTMLElement | null>(null);
    const [activeId, setActiveId] = useState<string | undefined>();

    const ids = headings.flatMap((heading) => [
        heading.id,
        ...heading.children.map(({ id }) => id),
    ]);

    const spiedActiveId = useScrollSpy(ids);

    const activeHeading = headings.find(
        (heading) =>
            heading.id === activeId || heading.children.some((child) => child.id === activeId),
    );
    const activeHeadingId = activeHeading ? activeHeading.id : undefined;

    const handleItemClick = (id: string) => {
        setActiveId(id);
        onItemSelected?.(id);
    };

    useEffect(() => {
        setActiveId(spiedActiveId);
    }, [spiedActiveId]);

    useLayoutEffect(() => {
        if (activeElement && 'scrollIntoViewIfNeeded' in activeElement) {
            (activeElement.scrollIntoViewIfNeeded as Function)({ behavior: 'smooth' });
        }
    }, [activeElement]);

    return (
        <Accordion.Root
            data-accordion
            type={expand ? 'multiple' : 'single'}
            className={className}
            collapsible
            value={(expand ? ids : activeHeadingId) as any}
        >
            {headings.map(({ id: headingId, title, children }) => {
                const isHeadingActive = headingId === activeId;

                return (
                    <Accordion.Item
                        key={headingId}
                        value={headingId}
                        data-accordion-item={headingId}
                    >
                        <Accordion.Header asChild>
                            <Link
                                data-accordion-item-header={slugify(title)}
                                className={classNames(styles.heading, styles.link, {
                                    [styles.active]: isHeadingActive,
                                    [styles.open]: expand
                                        ? ids.includes(headingId)
                                        : headingId === activeHeadingId,
                                })}
                                href={`#${headingId}`}
                                onClick={() => handleItemClick(headingId)}
                            >
                                <Accordion.Trigger
                                    className={styles.trigger}
                                    ref={isHeadingActive ? setActiveElement : undefined}
                                >
                                    <>
                                        {title}
                                        {children.length > 0 && (
                                            <LucideChevronRight
                                                className={styles.icon}
                                                width={12}
                                                height={12}
                                            />
                                        )}
                                    </>
                                </Accordion.Trigger>
                            </Link>
                        </Accordion.Header>
                        {children.length > 0 && (
                            <Accordion.Content
                                data-accordion-item-content
                                className={styles.content}
                            >
                                {children.map((child) => {
                                    const isActive = child.id === activeId;
                                    return (
                                        <a
                                            ref={isActive ? setActiveElement : undefined}
                                            key={child.id}
                                            className={classNames(styles.link, styles.nested, {
                                                [styles.active]: isActive,
                                            })}
                                            href={`#${child.id}`}
                                            onClick={() => handleItemClick(child.id)}
                                        >
                                            {child.title}
                                        </a>
                                    );
                                })}
                            </Accordion.Content>
                        )}
                    </Accordion.Item>
                );
            })}
        </Accordion.Root>
    );
}
